import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/components/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEditor, EditorContent } from '@tiptap/react';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Image as ImageIcon,
  Code,
  Send,
  Paperclip,
  FileUp,
  X,
  FileText,
  Mail,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content: string;
  category: 'merchant' | 'invoice' | 'general';
}

interface MerchantEmailTemplate {
  id: string;
  type: string;
  subject: string;
  content: string;
  enabled: boolean;
}

interface EmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSend: (data: {
    subject: string;
    content: string;
    to: string;
    attachments?: File[];
    sentViaSendGrid?: boolean;
  }) => Promise<void>;
  recipientEmail?: string;
  merchantName?: string;
  businessName?: string;
  applicationId?: string;
  applicationStatus?: string;
  bankingPartner?: string;
}

const MenuBar = ({ editor }: { editor: Editor | null }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!editor) return null;

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      editor.chain().focus().setImage({ src: url }).run();
    };
    reader.readAsDataURL(file);
  };

  const handleLinkAdd = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="border-b p-2 flex items-center gap-1 flex-wrap">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cn("h-8 w-8 p-0", editor.isActive('bold') && "bg-gray-200")}
      >
        <Bold className="h-4 w-4" />
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={cn("h-8 w-8 p-0", editor.isActive('italic') && "bg-gray-200")}
      >
        <Italic className="h-4 w-4" />
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={cn("h-8 w-8 p-0", editor.isActive('underline') && "bg-gray-200")}
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>
      
      <div className="w-px h-6 bg-gray-300 mx-1" />
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn("h-8 w-8 p-0", editor.isActive('bulletList') && "bg-gray-200")}
      >
        <List className="h-4 w-4" />
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={cn("h-8 w-8 p-0", editor.isActive('orderedList') && "bg-gray-200")}
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      
      <div className="w-px h-6 bg-gray-300 mx-1" />
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={cn("h-8 w-8 p-0", editor.isActive({ textAlign: 'left' }) && "bg-gray-200")}
      >
        <AlignLeft className="h-4 w-4" />
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={cn("h-8 w-8 p-0", editor.isActive({ textAlign: 'center' }) && "bg-gray-200")}
      >
        <AlignCenter className="h-4 w-4" />
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={cn("h-8 w-8 p-0", editor.isActive({ textAlign: 'right' }) && "bg-gray-200")}
      >
        <AlignRight className="h-4 w-4" />
      </Button>
      
      <div className="w-px h-6 bg-gray-300 mx-1" />
      
      <Button
        variant="ghost"
        size="sm"
        onClick={handleLinkAdd}
        className={cn("h-8 w-8 p-0", editor.isActive('link') && "bg-gray-200")}
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        className="h-8 w-8 p-0"
      >
        <ImageIcon className="h-4 w-4" />
      </Button>
      
      <div className="w-px h-6 bg-gray-300 mx-1" />
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={cn("h-8 w-8 p-0", editor.isActive('codeBlock') && "bg-gray-200")}
      >
        <Code className="h-4 w-4" />
      </Button>
    </div>
  );
};

export function EmailDialog({
  open,
  onOpenChange,
  onSend,
  recipientEmail = '',
  merchantName = '',
  businessName = '',
  applicationId = '',
  applicationStatus = 'pending',
  bankingPartner = '',
}: EmailDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const htmlInputRef = useRef<HTMLInputElement>(null);

  const [subject, setSubject] = useState('');
  const [to, setTo] = useState(recipientEmail);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('none');
  const [sending, setSending] = useState(false);
  const [sendViaSendGrid, setSendViaSendGrid] = useState(true);
  // Store the raw HTML content for merchant templates (preserves full email styling)
  const [rawHtmlContent, setRawHtmlContent] = useState<string | null>(null);
  const [isUsingTemplate, setIsUsingTemplate] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      Image,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
    ],
    content: '',
  });

  // Fetch merchant email templates from global merchantEmailTemplates collection
  const { data: merchantTemplates = [] } = useQuery<MerchantEmailTemplate[]>({
    queryKey: ['merchant-email-templates'],
    queryFn: async () => {
      const templatesRef = collection(db, 'merchantEmailTemplates');
      const snapshot = await getDocs(templatesRef);
      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      })) as MerchantEmailTemplate[];
    },
  });

  // Also fetch company-specific templates as fallback
  const { data: templates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ['emailTemplates', user?.companyId],
    queryFn: async () => {
      if (!user?.companyId) return [];

      const q = query(
        collection(db, 'emailTemplates'),
        where('companyId', '==', user.companyId),
        where('category', 'in', ['merchant', 'general'])
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      })) as EmailTemplate[];
    },
    enabled: !!user?.companyId,
  });

  // Template name mapping for merchant templates
  const templateNameMap: Record<string, string> = {
    'welcome': 'Welcome Email',
    'application-received': 'Application Received',
    'underwriting-update': 'Underwriting Update',
    'documents-required': 'Documents Required',
    'approved': 'Application Approved',
    'declined': 'Application Declined',
    'banking-partner-assigned': 'Banking Partner Assigned',
  };

  // Update recipient when prop changes
  useEffect(() => {
    setTo(recipientEmail);
  }, [recipientEmail]);

  // Replace template variables with actual data
  const replaceTemplateVariables = (text: string) => {
    const variables: Record<string, string> = {
      merchantName: merchantName || 'Valued Merchant',
      businessName: businessName || merchantName || 'Your Business',
      contactName: merchantName || 'Valued Merchant',
      currentDate: new Date().toLocaleDateString(),
      applicationDate: new Date().toLocaleDateString(),
      applicationStatus: applicationStatus || 'pending',
      updateMessage: `Your application status has been updated to ${applicationStatus}.`,
      platformName: 'Invoicer.gg',
      contactEmail: 'support@invoicer.gg',
      bankingPartner: bankingPartner || 'our banking partner',
      bankContact: 'Banking Support Team',
      bankSupportEmail: 'support@invoicer.gg',
    };

    let result = text;
    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    });
    return result;
  };

  // Load template when selected
  useEffect(() => {
    if (selectedTemplateId && selectedTemplateId !== 'none') {
      // First check merchant templates (prefixed with 'merchant_')
      if (selectedTemplateId.startsWith('merchant_')) {
        const templateType = selectedTemplateId.replace('merchant_', '');
        const template = merchantTemplates.find(t => t.type === templateType || t.id === templateType);
        if (template) {
          const content = replaceTemplateVariables(template.content);
          const subjectText = replaceTemplateVariables(template.subject);
          // Store raw HTML for merchant templates (preserves full email styling)
          setRawHtmlContent(content);
          setIsUsingTemplate(true);
          setSubject(subjectText);
          // Clear the editor since we're using HTML preview
          editor?.commands.setContent('');
        }
      } else {
        // Check company-specific templates (simple HTML, can use editor)
        const template = templates.find(t => t.id === selectedTemplateId);
        if (template) {
          const content = replaceTemplateVariables(template.content);
          const subjectText = replaceTemplateVariables(template.subject);
          setRawHtmlContent(null);
          setIsUsingTemplate(false);
          editor?.commands.setContent(content);
          setSubject(subjectText);
        }
      }
    } else {
      // None selected - clear everything and use editor
      setRawHtmlContent(null);
      setIsUsingTemplate(false);
    }
  }, [selectedTemplateId, templates, merchantTemplates, editor, merchantName, businessName, applicationStatus, bankingPartner]);

  const handleHTMLUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const html = e.target?.result as string;
      editor?.commands.setContent(html);
      toast({
        title: 'Success',
        description: 'HTML content loaded successfully',
      });
    };
    reader.readAsText(file);
  };

  const handleSend = async () => {
    if (!subject || !to) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    // Validate content exists
    const hasContent = isUsingTemplate ? !!rawHtmlContent : (editor && editor.getHTML() !== '<p></p>');
    if (!hasContent) {
      toast({
        title: 'Validation Error',
        description: 'Please add email content',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);
    try {
      // Use raw HTML for templates, editor content for custom emails
      const emailContent = isUsingTemplate && rawHtmlContent ? rawHtmlContent : editor?.getHTML() || '';

      // If SendGrid is checked and we have an applicationId, use the server API
      if (sendViaSendGrid && applicationId) {
        const response = await fetch('/api/merchant-applications/send-stage-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            applicationId,
            stage: applicationStatus || 'manual-email',
            subject,
            content: emailContent,
            recipientEmail: to, // Send the "To" field value as recipient
            triggeredBy: 'manual',
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to send email via SendGrid');
        }

        toast({
          title: 'Email Sent',
          description: `Email successfully delivered to ${result.recipient || to}`,
        });

        // Still call onSend to record in communications (with flag that it was already sent)
        await onSend({
          subject,
          content: emailContent,
          to,
          attachments,
          sentViaSendGrid: true,
        });
      } else {
        // Use the regular onSend handler (just records the email)
        await onSend({
          subject,
          content: emailContent,
          to,
          attachments,
          sentViaSendGrid: false,
        });
      }

      // Reset form
      setSubject('');
      setTo(recipientEmail);
      setAttachments([]);
      setSelectedTemplateId('none');
      setRawHtmlContent(null);
      setIsUsingTemplate(false);
      editor?.commands.setContent('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send email',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send Email</DialogTitle>
          <DialogDescription>
            Compose and send an email to the merchant
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template Selector */}
          <div>
            <Label>Use Template</Label>
            <Select
              value={selectedTemplateId}
              onValueChange={setSelectedTemplateId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a template (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None - Compose from scratch</SelectItem>

                {/* Merchant Email Templates from Config */}
                {merchantTemplates.filter(t => t.enabled).length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                      Merchant Templates
                    </div>
                    {merchantTemplates
                      .filter(t => t.enabled)
                      .map((template) => (
                        <SelectItem
                          key={`merchant_${template.type}`}
                          value={`merchant_${template.type}`}
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-blue-500" />
                            {templateNameMap[template.type] || template.type}
                          </div>
                        </SelectItem>
                      ))}
                  </>
                )}

                {/* Company-specific templates */}
                {templates.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                      Company Templates
                    </div>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          {template.name}
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {template.category}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* To Field */}
          <div>
            <Label>To</Label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
            />
          </div>

          {/* Subject Field */}
          <div>
            <Label>Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
            />
          </div>

          {/* Attachments */}
          <div>
            <Label>Attachments</Label>
            <div className="space-y-2">
              <Input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => {
                  if (e.target.files) {
                    setAttachments(Array.from(e.target.files));
                  }
                }}
                className="cursor-pointer"
              />
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {attachments.map((file, idx) => (
                    <Badge key={idx} variant="secondary">
                      <Paperclip className="h-3 w-3 mr-1" />
                      {file.name}
                      <button
                        type="button"
                        onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Email Content */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Email Content</Label>
              <div className="flex gap-2">
                {isUsingTemplate && (
                  <Badge variant="secondary" className="text-xs">
                    <Mail className="h-3 w-3 mr-1" />
                    Using Template
                  </Badge>
                )}
                {!isUsingTemplate && (
                  <>
                    <input
                      ref={htmlInputRef}
                      type="file"
                      accept=".html,.htm"
                      onChange={handleHTMLUpload}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => htmlInputRef.current?.click()}
                    >
                      <FileUp className="h-4 w-4 mr-2" />
                      Upload HTML
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Show HTML Preview for templates, TipTap editor for custom emails */}
            {isUsingTemplate && rawHtmlContent ? (
              <div className="border rounded-lg overflow-hidden bg-white">
                <div className="bg-gray-100 px-3 py-2 border-b text-xs text-gray-600 flex items-center gap-2">
                  <Mail className="h-3 w-3" />
                  Email Preview (template with full formatting)
                </div>
                <iframe
                  srcDoc={rawHtmlContent}
                  className="w-full min-h-[400px] border-0"
                  title="Email Preview"
                  sandbox="allow-same-origin"
                />
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <MenuBar editor={editor} />
                <EditorContent
                  editor={editor}
                  className="min-h-[300px] p-4 prose max-w-none"
                />
              </div>
            )}
          </div>

          {/* Send via SendGrid Option */}
          <div className="flex items-center space-x-2 py-2 px-3 bg-blue-50 rounded-lg border border-blue-200">
            <Checkbox
              id="sendViaSendGrid"
              checked={sendViaSendGrid}
              onCheckedChange={(checked) => setSendViaSendGrid(checked === true)}
            />
            <Label
              htmlFor="sendViaSendGrid"
              className="text-sm font-medium cursor-pointer flex items-center gap-2"
            >
              <Mail className="h-4 w-4 text-blue-500" />
              Send via SendGrid
              <span className="text-xs text-muted-foreground font-normal">
                (Email will be delivered to recipient)
              </span>
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending}>
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}